import { useState, useEffect, useRef, useCallback } from 'react';

interface UseSpeechToTextReturn {
  transcript: string;
  interimTranscript: string;
  isSupported: boolean;
  isRecognizing: boolean;
  startRecognition: () => void;
  stopRecognition: () => void;
  clearTranscript: () => void;
  ensureRecognitionRunning: () => void;
}

export const useSpeechToText = (): UseSpeechToTextReturn => {
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);

  const recognitionRef = useRef<any>(null);
  const isStartingRef = useRef(false);
  const shouldRestartRef = useRef(false); // Track if we should auto-restart

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn('⚠️ Speech Recognition not supported in this browser');
      console.log('💡 Use Chrome or Edge for speech-to-text');
      setIsSupported(false);
      return;
    }

    console.log('✅ Speech Recognition supported');
    setIsSupported(true);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'nl-NL';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      console.log('🎤 Recognition started');
      setIsRecognizing(true);
      isStartingRef.current = false;
    };

    recognition.onend = () => {
      console.log('🎤 Recognition ended');
      setIsRecognizing(false);
      isStartingRef.current = false;
      
      // Auto-restart if needed (keeps mic always listening)
      if (shouldRestartRef.current) {
        console.log('🔄 Auto-restarting recognition...');
        setTimeout(() => {
          if (shouldRestartRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              console.log('⚠️ Auto-restart failed, will retry on next cycle');
            }
          }
        }, 100); // Small delay to prevent rapid restart loops
      }
    };

    recognition.onresult = (event: any) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcriptText = result[0].transcript;

        if (result.isFinal) {
          final += transcriptText + ' ';
        } else {
          interim += transcriptText;
        }
      }

      if (final) {
        setTranscript(prev => prev + final);
        setInterimTranscript('');
        console.log('📝 Final transcript:', final.trim());
      } else {
        setInterimTranscript(interim);
      }
    };

    recognition.onerror = (event: any) => {
      // Ignore common non-critical errors
      if (event.error === 'no-speech') {
        console.log('🔇 No speech detected');
        return;
      }
      if (event.error === 'aborted') {
        console.log('🛑 Recognition aborted');
        return;
      }
      
      console.error('❌ Speech recognition error:', event.error);
      setIsRecognizing(false);
      isStartingRef.current = false;
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
    };
  }, []);

  const startRecognition = useCallback(() => {
    if (!recognitionRef.current || !isSupported) {
      console.warn('⚠️ Cannot start: recognition not available');
      return;
    }
    
    // Enable auto-restart
    shouldRestartRef.current = true;
    
    // Prevent multiple start attempts
    if (isStartingRef.current || isRecognizing) {
      console.log('⏭️ Recognition already starting or running');
      return;
    }

    try {
      isStartingRef.current = true;
      console.log('🎤 Starting recognition with auto-restart...');
      recognitionRef.current.start();
    } catch (error: any) {
      console.error('❌ Failed to start recognition:', error.message);
      isStartingRef.current = false;
      
      // If already started error, we're fine
      if (error.message?.includes('already started')) {
        setIsRecognizing(true);
      }
    }
  }, [isSupported, isRecognizing]);

  const stopRecognition = useCallback(() => {
    if (!recognitionRef.current) return;

    console.log('🛑 Stopping recognition and clearing transcript...');
    
    // 1. Disable auto-restart FIRST
    shouldRestartRef.current = false;
    
    // 2. Clear all transcript data IMMEDIATELY
    setTranscript('');
    setInterimTranscript('');
    setIsRecognizing(false);
    isStartingRef.current = false;

    // 3. Stop recognition
    try {
      recognitionRef.current.stop();
    } catch (error) {
      // Ignore errors when stopping
      console.log('⚠️ Stop error (may already be stopped)');
    }
    
    // 4. Force abort as backup (in case stop() doesn't work immediately)
    try {
      recognitionRef.current.abort();
    } catch (error) {
      // Ignore abort errors
    }
  }, []);

  const clearTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
  }, []);

  const ensureRecognitionRunning = useCallback(() => {
    if (!recognitionRef.current || !isSupported) return;
    
    // If not recognizing, force a restart
    if (!isRecognizing && !isStartingRef.current) {
      console.log('🔧 Recognition not running, forcing restart...');
      shouldRestartRef.current = true;
      try {
        isStartingRef.current = true;
        recognitionRef.current.start();
      } catch (error: any) {
        console.log('⚠️ Force restart error (may already be running):', error.message);
        isStartingRef.current = false;
        if (error.message?.includes('already started')) {
          setIsRecognizing(true);
        }
      }
    } else {
      console.log('✅ Recognition already running');
    }
  }, [isSupported, isRecognizing]);

  return {
    transcript,
    interimTranscript,
    isSupported,
    isRecognizing,
    startRecognition,
    stopRecognition,
    clearTranscript,
    ensureRecognitionRunning
  };
};
